/**
 * Savings goal — the prototype's "Set a goal" on the Savings page.
 *
 * INTERIM STORAGE, deliberately and visibly. The goal is a customer preference
 * with no money attached, and the API has no field for it yet; a
 * `savingsGoalCents` on /me with `PUT /me/savings-goal` is in the Codex brief
 * (2026-10-14). Until that lands the goal lives in localStorage keyed by user
 * id, wrapped in try/catch because storage can be absent or blocked, and is
 * read only here so the swap to the API is one file.
 */
const KEY = (userId: string) => `puntsave:savings-goal:${userId}`;

export interface SavingsGoal {
  targetCents: number;
  label: string;
}

export function readSavingsGoal(userId: string | undefined): SavingsGoal | null {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(KEY(userId));
    if (!raw) return null;
    const g = JSON.parse(raw) as Partial<SavingsGoal>;
    if (typeof g.targetCents !== 'number' || g.targetCents <= 0) return null;
    return { targetCents: Math.round(g.targetCents), label: typeof g.label === 'string' ? g.label : '' };
  } catch {
    return null;
  }
}

export function writeSavingsGoal(userId: string | undefined, goal: SavingsGoal | null): void {
  if (!userId) return;
  try {
    if (goal) localStorage.setItem(KEY(userId), JSON.stringify(goal));
    else localStorage.removeItem(KEY(userId));
  } catch {
    /* storage blocked — the goal simply does not persist */
  }
}

export interface GoalProgress {
  pct: number;
  toGoCents: number;
  reached: boolean;
}

/** Progress of locked Savings toward the target. Whole percent, clamped 0–100. */
export function goalProgress(savingsCents: number, goal: SavingsGoal): GoalProgress {
  const pct = goal.targetCents <= 0 ? 0 : Math.min(100, Math.max(0, Math.round((savingsCents / goal.targetCents) * 100)));
  return { pct, toGoCents: Math.max(0, goal.targetCents - savingsCents), reached: savingsCents >= goal.targetCents };
}
